/**
 * How complete a Bear session's data entry actually is.
 *
 * A session is typed in by one operator reading the in-game result screen,
 * often across two sittings, so "recorded" and "happened" routinely differ. The
 * server reports both sides of that gap:
 *
 *   participantCount      what the game said turned up
 *   recordedMemberCount   how many of them anyone has actually entered
 *
 * Showing only the first would present a quarter-entered session as a complete
 * one. Every total on a DRAFT session is a partial sum, and this component is
 * how that gets said out loud rather than left for the reader to infer.
 */

import type { BearSessionSummary } from '@kso/schemas';
import { Badge } from '../../components/ui/primitives';
import { formatNumber } from '../../lib/format';
import { tr, useT } from '../../i18n';

export function EntryStatusBadge({ status }: { status: BearSessionSummary['entryStatus'] }) {
  const t = useT();

  if (status === 'COMPLETE') {
    return (
      <Badge tone="CONFIRMED" title={t.entryProgress.completeTitle}>
        {t.entryProgress.complete}
      </Badge>
    );
  }
  return (
    <Badge tone="MEDIUM" title={t.entryProgress.stillBeingEnteredTitle}>
      {t.entryProgress.stillBeingEntered}
    </Badge>
  );
}

/**
 * @returns The fraction of expected participants actually recorded, or null
 *   when the game's participant count was never entered — in which case we
 *   genuinely cannot say how complete the entry is, and must not guess.
 */
export function entryCoverage(session: BearSessionSummary): number | null {
  if (session.participantCount === null || session.participantCount === 0) return null;
  return session.recordedMemberCount / session.participantCount;
}

export function EntryProgress({ session }: { session: BearSessionSummary }) {
  const t = useT();
  const coverage = entryCoverage(session);

  if (session.participantCount === null) {
    return (
      <span className="small" title={t.entryProgress.turnoutNotEnteredTitle}>
        {t.entryProgress.recordedOnly(formatNumber(session.recordedMemberCount) ?? '')}
        <span className="absent">{t.entryProgress.turnoutNotEntered}</span>
      </span>
    );
  }

  const tone = coverage === null ? 'UNKNOWN' : coverage >= 0.95 ? 'CONFIRMED' : coverage >= 0.5 ? 'MEDIUM' : 'LOW';
  const percent = coverage === null ? 0 : Math.round(coverage * 100);

  return (
    <span className="row" style={{ gap: 6 }}>
      <span className="small nowrap">
        {t.entryProgress.recordedOf(
          formatNumber(session.recordedMemberCount) ?? '',
          formatNumber(session.participantCount) ?? '',
        )}
      </span>
      {coverage !== null && coverage < 0.95 ? (
        <Badge tone={tone} title={t.entryProgress.percentEnteredTitle(percent)}>
          {t.entryProgress.percentEntered(percent)}
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * The caveat that has to accompany any total read off a partially-entered
 * session. Returns null when the session is complete and the numbers stand on
 * their own.
 *
 * A plain function rather than a component — its caller passes the result to
 * `Notice` — so it reads the active locale through `tr()` instead of a hook.
 */
export function partialTotalsCaveat(session: BearSessionSummary): string | null {
  const t = tr().entryProgress;
  const coverage = entryCoverage(session);

  if (session.entryStatus === 'DRAFT') {
    return coverage === null || session.participantCount === null
      ? t.draftNoCoverage
      : t.draftWithCoverage(session.recordedMemberCount, session.participantCount);
  }

  if (coverage !== null && coverage < 0.95 && session.participantCount !== null) {
    return t.completeButThin(session.recordedMemberCount, session.participantCount);
  }

  if (session.participantCount === null && session.recordedMemberCount > 0) {
    return t.noTurnoutRecorded;
  }

  return null;
}
